import React, { useState, useEffect } from 'react'
import Dropzone from 'react-dropzone'
import { remove } from 'local-storage'
import { useUserDispatcher } from '@hooks'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import { useMutation, useQuery, OperationVariables } from '@apollo/client'
import PublishIcon from '@material-ui/icons/Publish'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import DeleteIcon from '@material-ui/icons/Delete'
import Snackbar from '@material-ui/core/Snackbar'
import MuiAlert from '@material-ui/lab/Alert'
import { nanoid } from 'nanoid'
import { DropZoneArea, UploadArea, CloudUploadIcon } from './index.sc'
import { UploadFileOption, uploadFileAsync } from '@utils/awsHelper'
import { CreateMediaConvertJob, RegisterVideoProgress, CreateVideoMaterial, JOBCONVERTSTATUS, VIDEOTYPE, UPLOADSTATUS } from '@services/app/mutation'
import appClient from '@services'
import { IServiceResponse } from '@services/model'

import VideoProgress from './localComponents/VideoProgress'
import { getVideoProgresses, postVideoProgress } from '../../services/restAPI/videoProgress/videoProgress'
import { postMediaConvertJob, postVideoMaterial } from '../../services/restAPI/videoMaterial/videoMaterialService'

interface UploadPercentage {
  key: string,
  prctg: number
}

function Alert(props) {
  return <MuiAlert elevation={6} variant="filled" {...props} />
}

const objectUrl = null

const UploadFile: React.FC = () => {
  const { clearUserAuthData } = useUserDispatcher()
  const [files, setFiles] = useState<File[]>([])
  const [uploadPrctgs, setUploadPrctgs] = useState<UploadPercentage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [openSnackBar, setOpenSnackbar] = useState(false)
  const [registerVideoProgress] = useMutation(RegisterVideoProgress, { client: appClient })
  const [createVideoMaterial] = useMutation(CreateVideoMaterial, { client: appClient })
  const [createMediaConvertJob] = useMutation(CreateMediaConvertJob, { client: appClient })
  const [videoProgresses, setVideoProgresses] = useState<any[]>([])

  const goToLogin = () => {
    remove('token')
    clearUserAuthData()
  }

  const handleRetrieveVideoProgress = async () => {
    getVideoProgresses()
      .then(response => {
        setVideoProgresses(response)
      })
      .catch(err => {
        if (err.message == "authorization") {
          goToLogin()
        }
      })
  }

  useEffect(() => {
    handleRetrieveVideoProgress()
  }, [])

  const loadVideo = file => new Promise((resolve, reject) => {
    try {
      const video = document.createElement('video')
      video.preload = 'metadata'

      video.onloadedmetadata = function () {
        resolve(this)
      }

      video.onerror = function () {
        reject('Invalid video. Please select a video file.')
      }

      video.src = window.URL.createObjectURL(file)
    } catch (e) {
      reject(e)
    }
  })

  const handleFileDrop = async (files: File[]) => {
    for (const file of files) {
      setUploadPrctgs(prevData => {
        return [...prevData, {
          key: file.name,
          prctg: 0
        }]
      })

    }
    setFiles(files)
  }

  const handleUploadFile = async () => {
    try {
      setIsLoading(true)
      for (const targetFile of files) {
        //** 1. Upload File To S3 Input Bucket */     
        const fileName = targetFile.name
        const extension = fileName.split('.')[1]
        // const sanitizeFileName = fileName.trim().replaceAll(' ', '-') 
        // const folderName = sanitizeFileName.split('.')[0]    
        const newId = nanoid()
        const sanitizeFileName = `${newId}.${extension}`
        const folderName = newId
        const option: UploadFileOption = {
          name: sanitizeFileName,
          pathToStore: `videos/materials/${folderName}`
        }

        const progressEventCallback = (progress: number) => {
          setUploadPrctgs(prevData => {
            const fileData = uploadPrctgs.find(x => x.key == targetFile.name)
            fileData.prctg = progress
            const newColls = prevData.filter(x => x.key != targetFile.name)
            return [...newColls, fileData]
          })
        }

        const uploadResult = await uploadFileAsync(targetFile, option, progressEventCallback)

        // ** 2. Trigger Video Convert Process */ 
        const createMCJobVariables = {
          fileKeyPath: uploadResult.Key
        }
        const createMediaConvertJobResponse = await postMediaConvertJob(createMCJobVariables)

        // ** 3. Save to Video Material */  .            
        let millisecondsDuration = 0
        try {
          const targetVideoFile = await loadVideo(targetFile)
          millisecondsDuration = parseInt((targetVideoFile.duration * 1000).toFixed(0))
        } catch (err) {
          if (err?.graphQLErrors[0]?.extensions?.code == "authorization") {
            goToLogin()
            return
          }
          console.error(err)
        }

        const createVideoMaterialVariables = {
          name: targetFile.name,
          sequence: 0, // TODO: need to create field to input or auto increment
          videoDuration: millisecondsDuration,
          videoType: VIDEOTYPE.SUPPORTMATERIAL, // TODO: need to create dropdown for this
          videoURL: `${uploadResult.Key.split('.')[0]}.m3u8`,
          inputVideoURL: `${uploadResult.Key}`,
        }

        const createVideoMaterialResponse = await postVideoMaterial(createVideoMaterialVariables)

        // ** Save Video Progress
        const registerVideoProgressVariables = {
          videoId: createVideoMaterialResponse.id,
          uploadId: uploadResult.ETag?.replaceAll('"', ''),
          uploadStatus: UPLOADSTATUS.UPLOADED,
          jobConvertId: createMediaConvertJobResponse.jobId,
          jobConvertStatus: JOBCONVERTSTATUS.PROGRESSING,
          videoType: VIDEOTYPE.SUPPORTMATERIAL,
        }

        await postVideoProgress(registerVideoProgressVariables)

        await handleRetrieveVideoProgress()
      }

      setFiles([])
      setUploadPrctgs([])
      setOpenSnackbar(true)
      setIsLoading(false)
    } catch (err) {
      if (err?.graphQLErrors[0]?.extensions?.code == "authorization") {
        goToLogin()
        return
      }

      setIsLoading(false)
      console.error(err)
    }
  }

  const handleDeleteItem = (file: File) => {
    setFiles(prevFiles => {
      return prevFiles.filter(f => f.name != file.name)
    })
  }

  const getPercentage = (file: File) => {
    if (uploadPrctgs === undefined) return 0

    return uploadPrctgs?.find(x => x.key == file.name)?.prctg || 0
  }

  const handleOnCloseSnackbar = () => {
    setOpenSnackbar(false)
  }

  return <div>
    <h3>Upload Video Material</h3>
    {/* <audio style={{display: 'none'}} id="audio"></audio> */}
    <Snackbar open={openSnackBar} onClose={handleOnCloseSnackbar} autoHideDuration={6000}>
      <Alert onClose={handleOnCloseSnackbar} severity="success">
        Video(s) have successfully uploaded!
      </Alert>
    </Snackbar>
    <Dropzone
      onDrop={handleFileDrop}
      multiple
      accept="video/*"
      disabled={isLoading}
    >
      {({ getRootProps, getInputProps }) => (
        <DropZoneArea {...getRootProps()} style={{ background: '#fff' }}>
          <input {...getInputProps()} />
          <UploadArea>
            <div>
              <CloudUploadIcon />
            </div>
            <p>Click to choose your video file(s)</p>
          </UploadArea>
        </DropZoneArea>
      )}
    </Dropzone>
    <div>
      <h4>Selected Files</h4>
      <List>
        {files.length == 0 && <div>-No File selected-</div>}
        {files.map(file => {
          return (<ListItem
            key={file.name}
            style={{
              border: '1px dashed #939393',
              borderRadius: '5px',
              width: '25%',
              justifyContent: 'space-between',
              marginBottom: 10
            }}
          >
            <div
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 10 }}>
                <h4>Name:</h4>
                <span>{file.name}</span>
              </div>
              {isLoading && (<div style={{ display: 'flex', flexDirection: 'column' }}>
                <h4>Progress:</h4>
                <span style={{ color: '#e91e63', fontWeight: 'bold' }}>{getPercentage(file)} %</span>
              </div>)}
            </div>
            <div>
              <IconButton disabled={isLoading} aria-label="delete" size="small" color="secondary" onClick={() => handleDeleteItem(file)}>
                <DeleteIcon />
              </IconButton>
            </div>
          </ListItem>)
        })}
      </List>
    </div>
    <Button
      variant="contained"
      color="primary"
      startIcon={<PublishIcon />}
      onClick={handleUploadFile}
      style={{ marginTop: 10 }}
      disabled={files.length === 0 || isLoading}
    >
      Upload
    </Button>
    <hr />
    <h3>Video Progress Information</h3>
    <VideoProgress videoProgresses={videoProgresses} />
  </div>
}

export default UploadFile